import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';

// Cache pour éviter le tracking en double (même utilisateur consulte plusieurs fois en peu de temps)
const recentlyTracked = new Map<string, number>();
const TRACKING_COOLDOWN = 5 * 60 * 1000; // 5 minutes

interface ProfileViewDetail {
  user_id: string;
  created_at: string;
  ip_address?: string;
  user_agent?: string;
}

interface TopViewedProfile {
  member_id: string;
  view_count: number;
  member_name?: string;
  member_image?: string;
}

export const useProfileViews = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();

  // Tracker une vue de profil
  const trackProfileView = async (memberId: string) => {
    if (!user) {
      console.log('[useProfileViews] No user authenticated, skipping tracking');
      return;
    }

    // Récupérer le member complet pour avoir le user_id
    const { data: memberData } = await supabase
      .from('club_members')
      .select('user_id')
      .eq('id', memberId)
      .single();

    // Ne pas tracker si c'est son propre profil
    if (memberData?.user_id === user.id) {
      console.log('[useProfileViews] Skipping self-view tracking');
      return;
    }

    // Vérifier le cooldown anti-spam
    const cacheKey = `${user.id}-${memberId}`;
    const lastTracked = recentlyTracked.get(cacheKey);
    const now = Date.now();

    if (lastTracked && (now - lastTracked) < TRACKING_COOLDOWN) {
      console.log('[useProfileViews] Cooldown active, skipping tracking');
      return;
    }

    try {
      const { error } = await supabase
        .from('data_access_logs')
        .insert({
          user_id: user.id,
          accessed_member_id: memberId,
          access_type: 'profile_view'
        });

      if (error) {
        console.error('[useProfileViews] Error tracking view:', error);
      } else {
        // Mettre à jour le cache
        recentlyTracked.set(cacheKey, now);
        
        // Invalider les queries de comptage pour forcer le refresh
        queryClient.invalidateQueries({ queryKey: ['profile-view-count', memberId] });
        queryClient.invalidateQueries({ queryKey: ['top-viewed-profiles'] });
        
        console.log('[useProfileViews] View tracked successfully for member:', memberId);
      }
    } catch (error) {
      console.error('[useProfileViews] Exception tracking view:', error);
    }
  };

  // Récupérer le nombre de vues d'un profil (accessible à tous)
  const useProfileViewCount = (memberId?: string) => {
    return useQuery({
      queryKey: ['profile-view-count', memberId],
      queryFn: async () => {
        if (!memberId) return 0;

        const { count, error } = await supabase
          .from('data_access_logs')
          .select('*', { count: 'exact', head: true })
          .eq('accessed_member_id', memberId)
          .eq('access_type', 'profile_view');

        if (error) {
          console.error('[useProfileViews] Error getting view count:', error);
          return 0;
        }

        return count || 0;
      },
      enabled: !!memberId,
      staleTime: 60 * 1000, // Cache pendant 1 minute
    });
  };

  // Récupérer les détails des vues (admins seulement)
  const useProfileViewDetails = (memberId?: string) => {
    const isAdmin = hasPermission('view_analytics');

    return useQuery({
      queryKey: ['profile-view-details', memberId],
      queryFn: async () => {
        if (!memberId || !isAdmin) return [];

        const { data, error } = await supabase
          .from('data_access_logs')
          .select(`
            user_id,
            created_at,
            ip_address,
            user_agent
          `)
          .eq('accessed_member_id', memberId)
          .eq('access_type', 'profile_view')
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          console.error('[useProfileViews] Error getting view details:', error);
          return [];
        }

        return data as ProfileViewDetail[];
      },
      enabled: !!memberId && isAdmin,
      staleTime: 5 * 60 * 1000, // Cache pendant 5 minutes
    });
  };

  // Récupérer les profils les plus vus (admins seulement)
  const useTopViewedProfiles = (limit: number = 10) => {
    const isAdmin = hasPermission('view_analytics');

    return useQuery({
      queryKey: ['top-viewed-profiles', limit],
      queryFn: async () => {
        if (!isAdmin) return [];

        // Récupérer les vues groupées par member
        const { data: viewsData, error: viewsError } = await supabase
          .from('data_access_logs')
          .select('accessed_member_id')
          .eq('access_type', 'profile_view');

        if (viewsError) {
          console.error('[useProfileViews] Error getting top viewed:', viewsError);
          return [];
        }

        // Compter les vues par membre
        const viewCounts = viewsData.reduce((acc, log) => {
          const memberId = log.accessed_member_id;
          acc[memberId] = (acc[memberId] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Trier et limiter
        const topMembers = Object.entries(viewCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, limit)
          .map(([memberId, count]) => ({
            member_id: memberId,
            view_count: count
          }));

        // Enrichir avec les données des membres
        const memberIds = topMembers.map(m => m.member_id);
        const { data: membersData, error: membersError } = await supabase
          .from('club_members')
          .select('id, name, image')
          .in('id', memberIds);

        if (membersError) {
          console.error('[useProfileViews] Error getting member data:', membersError);
          return topMembers;
        }

        // Combiner les données
        return topMembers.map(top => {
          const member = membersData?.find(m => m.id === top.member_id);
          return {
            ...top,
            member_name: member?.name,
            member_image: member?.image
          };
        }) as TopViewedProfile[];
      },
      enabled: isAdmin,
      staleTime: 5 * 60 * 1000, // Cache pendant 5 minutes
    });
  };

  return {
    trackProfileView,
    useProfileViewCount,
    useProfileViewDetails,
    useTopViewedProfiles
  };
};
